export type AppointmentCompletionRequirementKey =
  | 'photos'
  | 'signature'
  | 'odometer'
  | 'expenses'
  | 'public_comments';

export type AppointmentDetailTabKey = 'work-breakdown' | 'photos' | 'comments' | 'materials';

export interface AppointmentCompletionRequirementOverride {
  enabled?: boolean;
  reason?: string;
  approvedByUserId?: number | string | null;
  approvedByName?: string | null;
  approvedAt?: string | null;
}

export interface AppointmentCompletionRulesOverride {
  enabled?: boolean;
  reason?: string;
  approvedByUserId?: number | string | null;
  approvedByName?: string | null;
  approvedAt?: string | null;
}

export type AppointmentCompletionRequirementOverrideMap = Partial<
  Record<AppointmentCompletionRequirementKey, AppointmentCompletionRequirementOverride>
>;

export interface AppointmentTypeRulesConfig {
  completionRequirements?: AppointmentCompletionRequirementKey[];
  allowAdminOverrideRequirements?: AppointmentCompletionRequirementKey[];
  hiddenTabs?: AppointmentDetailTabKey[];
}

export interface ConfigurableTypeOption {
  key: string;
  label: string;
  icon?: string;
  color?: string;
  rules?: AppointmentTypeRulesConfig;
  visible_to_customer?: boolean;
}

export interface AppointmentTypeAwareWorkItem {
  item_type?: string | null;
  item_subtype?: string | null;
  appointment_type?: string | null;
  custom_fields?: Record<string, any> | null;
}

export const PURCHASE_APPOINTMENT_TYPE_KEY = 'purchase';

export const DEFAULT_JOB_TYPES: ConfigurableTypeOption[] = [
  { key: 'service_call', label: 'Service Call', icon: 'Phone', color: '#3b82f6' },
  { key: 'maintenance', label: 'Maintenance', icon: 'Wrench', color: '#10b981' },
  { key: 'repair', label: 'Repair', icon: 'Settings', color: '#f59e0b' },
  { key: 'installation', label: 'Installation', icon: 'Package', color: '#8b5cf6' },
  { key: 'inspection', label: 'Inspection', icon: 'Search', color: '#06b6d4' },
  { key: 'cleaning', label: 'Cleaning', icon: 'Sparkles', color: '#ec4899' },
  { key: 'other', label: 'Other', icon: 'MoreHorizontal', color: '#6b7280' },
];

const DEFAULT_APPOINTMENT_RULES: Required<AppointmentTypeRulesConfig> = {
  completionRequirements: ['photos', 'signature', 'odometer', 'public_comments'],
  allowAdminOverrideRequirements: [],
  hiddenTabs: [],
};

const PURCHASE_APPOINTMENT_RULES: Required<AppointmentTypeRulesConfig> = {
  completionRequirements: ['expenses', 'odometer'],
  allowAdminOverrideRequirements: [],
  hiddenTabs: ['work-breakdown', 'photos', 'materials'],
};

const INHERITED_PARENT_JOB_OVERRIDE_REASON_PREFIX = 'inherited from parent job';

export const DEFAULT_APPOINTMENT_TYPES: ConfigurableTypeOption[] = [
  { key: 'site_visit', label: 'Site Visit', icon: 'Calendar', color: '#10b981' },
  { key: 'purchase', label: 'Purchase', icon: 'ShoppingBag', color: '#14b8a6', rules: PURCHASE_APPOINTMENT_RULES },
  { key: 'installation', label: 'Installation', icon: 'Wrench', color: '#3b82f6' },
  { key: 'repair', label: 'Repair', icon: 'Settings', color: '#f59e0b' },
  { key: 'maintenance', label: 'Maintenance', icon: 'Shield', color: '#8b5cf6' },
  { key: 'inspection', label: 'Inspection', icon: 'Search', color: '#06b6d4' },
  { key: 'follow_up', label: 'Follow-up', icon: 'RefreshCw', color: '#ec4899' },
  { key: 'emergency', label: 'Emergency', icon: 'AlertTriangle', color: '#ef4444' },
];

function uniquePreserveOrder<T>(values: T[]): T[] {
  return Array.from(new Set(values));
}

function normalizeCompletionRequirementKey(
  value?: string | null
): AppointmentCompletionRequirementKey | null {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

  switch (normalized) {
    case 'photos':
    case 'signature':
    case 'odometer':
    case 'expenses':
    case 'public_comments':
      return normalized;
    default:
      return null;
  }
}

function normalizeAppointmentDetailTabKey(
  value?: string | null
): AppointmentDetailTabKey | null {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-');

  switch (normalized) {
    case 'work-breakdown':
    case 'photos':
    case 'comments':
    case 'materials':
      return normalized;
    default:
      return null;
  }
}

export function normalizeAppointmentTypeKey(value?: string | null): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
}

export function getDefaultAppointmentTypeRules(
  typeKey?: string | null
): Required<AppointmentTypeRulesConfig> {
  return normalizeAppointmentTypeKey(typeKey) === PURCHASE_APPOINTMENT_TYPE_KEY
    ? { ...PURCHASE_APPOINTMENT_RULES }
    : { ...DEFAULT_APPOINTMENT_RULES };
}

export function normalizeAppointmentTypeRules(
  typeKey?: string | null,
  rules?: AppointmentTypeRulesConfig | null
): Required<AppointmentTypeRulesConfig> {
  const normalizedTypeKey = normalizeAppointmentTypeKey(typeKey);
  const defaults = getDefaultAppointmentTypeRules(normalizedTypeKey);

  const completionRequirements = uniquePreserveOrder(
    (rules?.completionRequirements || defaults.completionRequirements)
      .map(normalizeCompletionRequirementKey)
      .filter(
        (value): value is AppointmentCompletionRequirementKey => Boolean(value)
      )
  );

  const allowAdminOverrideRequirements = uniquePreserveOrder(
    (rules?.allowAdminOverrideRequirements || defaults.allowAdminOverrideRequirements)
      .map(normalizeCompletionRequirementKey)
      .filter(
        (value): value is AppointmentCompletionRequirementKey => Boolean(value)
      )
  );

  const hiddenTabs = uniquePreserveOrder(
    (rules?.hiddenTabs || defaults.hiddenTabs)
      .map(normalizeAppointmentDetailTabKey)
      .filter((value): value is AppointmentDetailTabKey => Boolean(value))
  );

  const normalizedHiddenTabs: AppointmentDetailTabKey[] = normalizedTypeKey === PURCHASE_APPOINTMENT_TYPE_KEY
    ? uniquePreserveOrder<AppointmentDetailTabKey>([
        ...hiddenTabs.filter((value) => value !== 'comments'),
        'materials',
      ])
    : hiddenTabs;

  const normalizedCompletionRequirements = (
    normalizedHiddenTabs.includes('comments')
      ? completionRequirements.filter((value) => value !== 'public_comments')
      : completionRequirements
  ).filter((value) => {
    if (normalizedTypeKey !== PURCHASE_APPOINTMENT_TYPE_KEY) {
      return true;
    }

    return value !== 'public_comments';
  });

  const normalizedAllowAdminOverrideRequirements = allowAdminOverrideRequirements.filter(
    (value) => normalizedCompletionRequirements.includes(value)
  );

  return {
    completionRequirements: normalizedCompletionRequirements,
    allowAdminOverrideRequirements: normalizedAllowAdminOverrideRequirements,
    hiddenTabs: normalizedHiddenTabs,
  };
}

export function normalizeTypeOptionsForItemType(
  itemType: string,
  typeOptions?: ConfigurableTypeOption[] | null
): ConfigurableTypeOption[] {
  const source = Array.isArray(typeOptions) && typeOptions.length > 0
    ? typeOptions
    : itemType === 'appointment'
      ? DEFAULT_APPOINTMENT_TYPES
      : DEFAULT_JOB_TYPES;

  if (itemType !== 'appointment') {
    return source.map((typeOption) => ({ ...typeOption }));
  }

  return source.map((typeOption) => {
    const normalizedKey = normalizeAppointmentTypeKey(typeOption.key);
    return {
      ...typeOption,
      key: normalizedKey,
      rules: normalizeAppointmentTypeRules(normalizedKey, typeOption.rules),
    };
  });
}

export function getDefaultTypeOptions(itemType: string): ConfigurableTypeOption[] {
  return normalizeTypeOptionsForItemType(itemType);
}

export function getAppointmentTypeKey(
  workItem: AppointmentTypeAwareWorkItem | null | undefined
): string {
  if (!workItem || workItem.item_type !== 'appointment') {
    return '';
  }

  const candidates = [
    workItem.appointment_type,
    workItem.item_subtype,
    workItem.custom_fields?.appointment_type,
    workItem.custom_fields?.item_subtype,
    workItem.custom_fields?.subtype,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeAppointmentTypeKey(
      typeof candidate === 'string' ? candidate : undefined
    );

    if (normalized) {
      return normalized;
    }
  }

  return '';
}

export function getAppointmentTypeOption(
  appointmentTypes: ConfigurableTypeOption[] | null | undefined,
  workItem: AppointmentTypeAwareWorkItem | null | undefined
): ConfigurableTypeOption | null {
  const appointmentTypeKey = getAppointmentTypeKey(workItem);
  if (!appointmentTypeKey) {
    return null;
  }

  const normalizedTypeOptions = normalizeTypeOptionsForItemType(
    'appointment',
    appointmentTypes
  );

  return (
    normalizedTypeOptions.find(
      (typeOption) => normalizeAppointmentTypeKey(typeOption.key) === appointmentTypeKey
    ) || null
  );
}

export function getAppointmentTypeRules(
  workItem: AppointmentTypeAwareWorkItem | null | undefined,
  appointmentTypes?: ConfigurableTypeOption[] | null
): Required<AppointmentTypeRulesConfig> {
  if (!workItem || workItem.item_type !== 'appointment') {
    return {
      completionRequirements: [],
      allowAdminOverrideRequirements: [],
      hiddenTabs: [],
    };
  }

  const appointmentTypeKey = getAppointmentTypeKey(workItem);
  const typeOption = getAppointmentTypeOption(appointmentTypes || null, workItem);

  return normalizeAppointmentTypeRules(appointmentTypeKey, typeOption?.rules);
}

export function isPurchaseAppointment(
  workItem: AppointmentTypeAwareWorkItem | null | undefined
): boolean {
  return getAppointmentTypeKey(workItem) === PURCHASE_APPOINTMENT_TYPE_KEY;
}

export function getAppointmentCompletionRequirementKeys(
  workItem: AppointmentTypeAwareWorkItem | null | undefined,
  appointmentTypes?: ConfigurableTypeOption[] | null
): AppointmentCompletionRequirementKey[] {
  return getAppointmentTypeRules(workItem, appointmentTypes).completionRequirements;
}

export function getOverridableAppointmentCompletionRequirementKeys(
  workItem: AppointmentTypeAwareWorkItem | null | undefined,
  appointmentTypes?: ConfigurableTypeOption[] | null
): AppointmentCompletionRequirementKey[] {
  return getAppointmentTypeRules(workItem, appointmentTypes).allowAdminOverrideRequirements;
}

export function appointmentTypeAllowsCompletionRequirementOverride(
  workItem: AppointmentTypeAwareWorkItem | null | undefined,
  requirement: AppointmentCompletionRequirementKey,
  appointmentTypes?: ConfigurableTypeOption[] | null
): boolean {
  return getOverridableAppointmentCompletionRequirementKeys(
    workItem,
    appointmentTypes
  ).includes(requirement);
}

function normalizeEnabledReasonOverride(
  value: unknown
): AppointmentCompletionRequirementOverride | AppointmentCompletionRulesOverride | null {
  if (typeof value === 'string') {
    const reason = value.trim();
    if (isInheritedParentJobCompletionOverrideReason(reason)) {
      return null;
    }
    return reason ? { enabled: true, reason } : null;
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;
  const reason = typeof candidate.reason === 'string' ? candidate.reason.trim() : '';
  const enabled = candidate.enabled !== false;

  if (!enabled || !reason || isInheritedParentJobCompletionOverrideReason(reason)) {
    return null;
  }

  return {
    enabled: true,
    reason,
    approvedByUserId: candidate.approvedByUserId as number | string | null | undefined,
    approvedByName: typeof candidate.approvedByName === 'string' ? candidate.approvedByName : null,
    approvedAt: typeof candidate.approvedAt === 'string' ? candidate.approvedAt : null,
  };
}

export function isInheritedParentJobCompletionOverrideReason(
  value: unknown
): boolean {
  return typeof value === 'string'
    && value.trim().toLowerCase().startsWith(INHERITED_PARENT_JOB_OVERRIDE_REASON_PREFIX);
}

export function getAppointmentCompletionRequirementOverrides(
  workItem: AppointmentTypeAwareWorkItem | null | undefined,
  appointmentTypes?: ConfigurableTypeOption[] | null
): AppointmentCompletionRequirementOverrideMap {
  if (!workItem || workItem.item_type !== 'appointment') {
    return {};
  }

  const allowedRequirements = new Set(
    getOverridableAppointmentCompletionRequirementKeys(workItem, appointmentTypes)
  );

  if (allowedRequirements.size === 0) {
    return {};
  }

  const rawOverrides = workItem.custom_fields?.completion_requirement_overrides;
  if (!rawOverrides || typeof rawOverrides !== 'object') {
    return {};
  }

  const normalizedOverrides: AppointmentCompletionRequirementOverrideMap = {};

  Object.entries(rawOverrides as Record<string, unknown>).forEach(([rawKey, rawValue]) => {
    const key = normalizeCompletionRequirementKey(rawKey);
    if (!key || !allowedRequirements.has(key)) {
      return;
    }

    const normalizedOverride = normalizeEnabledReasonOverride(rawValue);
    if (!normalizedOverride) {
      return;
    }

    normalizedOverrides[key] = normalizedOverride;
  });

  return normalizedOverrides;
}

export function getAppointmentCompletionRequirementOverride(
  workItem: AppointmentTypeAwareWorkItem | null | undefined,
  requirement: AppointmentCompletionRequirementKey,
  appointmentTypes?: ConfigurableTypeOption[] | null
): AppointmentCompletionRequirementOverride | null {
  return getAppointmentCompletionRequirementOverrides(workItem, appointmentTypes)[requirement] || null;
}

export function appointmentCompletionRequirementIsOverridden(
  workItem: AppointmentTypeAwareWorkItem | null | undefined,
  requirement: AppointmentCompletionRequirementKey,
  appointmentTypes?: ConfigurableTypeOption[] | null
): boolean {
  return Boolean(
    getAppointmentCompletionRequirementOverride(workItem, requirement, appointmentTypes)
  );
}

export function appointmentTypeHasRequirement(
  workItem: AppointmentTypeAwareWorkItem | null | undefined,
  requirement: AppointmentCompletionRequirementKey,
  appointmentTypes?: ConfigurableTypeOption[] | null
): boolean {
  return getAppointmentCompletionRequirementKeys(workItem, appointmentTypes).includes(
    requirement
  );
}

export function getHiddenAppointmentTabs(
  workItem: AppointmentTypeAwareWorkItem | null | undefined,
  appointmentTypes?: ConfigurableTypeOption[] | null
): AppointmentDetailTabKey[] {
  return getAppointmentTypeRules(workItem, appointmentTypes).hiddenTabs;
}

export function appointmentTypeHidesTab(
  workItem: AppointmentTypeAwareWorkItem | null | undefined,
  tabKey: AppointmentDetailTabKey,
  appointmentTypes?: ConfigurableTypeOption[] | null
): boolean {
  return getHiddenAppointmentTabs(workItem, appointmentTypes).includes(tabKey);
}

export function shouldRequireAppointmentPublicClosingComment(
  workItem: AppointmentTypeAwareWorkItem | null | undefined,
  appointmentTypes?: ConfigurableTypeOption[] | null
): boolean {
  return appointmentTypeHasRequirement(
    workItem,
    'public_comments',
    appointmentTypes
  );
}

export function getAppointmentCompletionRulesOverride(
  workItem: AppointmentTypeAwareWorkItem | null | undefined
): AppointmentCompletionRulesOverride | null {
  if (!workItem || workItem.item_type !== 'appointment') {
    return null;
  }

  return normalizeEnabledReasonOverride(workItem.custom_fields?.completion_rules_override);
}

export function appointmentCompletionRulesAreOverridden(
  workItem: AppointmentTypeAwareWorkItem | null | undefined
): boolean {
  return Boolean(getAppointmentCompletionRulesOverride(workItem));
}

/**
 * Returns the set of type keys where `visible_to_customer === false`.
 * Works for any item type (appointment, job, etc.) since ConfigurableTypeOption
 * is the shared interface for all configurable type options.
 */
export function getHiddenTypeKeys(typeOptions: ConfigurableTypeOption[]): string[] {
  return typeOptions
    .filter((opt) => opt.visible_to_customer === false)
    .map((opt) => normalizeAppointmentTypeKey(opt.key))
    .filter(Boolean);
}
